"use client";

import BFLLink from "@repo/shared-ui/BFLLink";
import BFLHoverBackdrop from "@repo/shared-ui/BFLHoverBackdrop";
import BFLImage from "@repo/shared-ui/BFLImage";
import { useState } from "react";
import Conditional from "@repo/shared-ui/Conditional";
import { handleHeaderPartnerClickDL } from "./HeaderAnalyticsFunction";

interface Partner {
  ctaInNewTab: string;
  ctaLink: string;
  image: string;
  imageAltText: string;
  label: string;
  nodeName?: string;
}

interface PartnerDataProps {
  partnerData: any;
}

const PartnerLinksMenu = ({ partnerData }: PartnerDataProps) => {
  const [isPartnerOpen, setIsPartnerOpen] = useState(false);
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);

  // Handle different partner data structures
  let partners: Partner[] = [];

  // New partner data format - where partnerDetails is nested under partnerData.children
  if (partnerData?.children) {
    const partnerDetails = partnerData.children.find(
      (child: any) => child.nodeName === "partnerDetails",
    );
    if (partnerDetails?.children) {
      partners = partnerDetails.children.filter((item: any) =>
        item.nodeName?.startsWith("item"),
      );
    }
  }
  // Direct partnerDetails structure
  else if (
    partnerData?.nodeName === "partnerDetails" &&
    Array.isArray(partnerData?.children)
  ) {
    partners = partnerData.children.filter((item: any) =>
      item.nodeName?.startsWith("item"),
    );
  }
  // Legacy partner data format
  else if (partnerData?.[0]) {
    partners = Object.values(partnerData[0]);
  }

  const handlePartnerHover = (showOrHide: boolean) => {
    setIsPartnerOpen(showOrHide);
    if (!hasOpenedOnce) {
      setHasOpenedOnce(true); // Load image only once
    }
  };

  return (
    <nav
      className="group relative order-6 inline-block md:order-6"
      onMouseEnter={() => handlePartnerHover(true)}
      onMouseLeave={() => handlePartnerHover(false)}
      aria-label="Partner links"
    >
      <BFLHoverBackdrop zIndex={40} headerHeight={87}>
        <button
          onClick={() => handleHeaderPartnerClickDL("Partners")}
          className={`my-0 hidden cursor-pointer flex-col items-center px-2 pb-[30px] pt-[14px] md:!flex ${isPartnerOpen ? "bg-[#001326]" : ""} hover:bg-[#001326]`}
          aria-label="Partners"
        >
          <i className="icomoon text-2xl before:content-['\e935']"></i>
          <span className="hidden items-center text-xs leading-[14px] text-white md:!inline-flex">
            Partners{" "}
            <i className="icomoon text-xs transition-transform before:content-['\e981'] group-hover:-rotate-180"></i>
          </span>
        </button>

        <div
          className={`z-99 absolute right-0 top-20 w-64 bg-[#000f1e] p-4 text-sm shadow-lg transition-all duration-300 ${isPartnerOpen ? "visible" : "invisible"}`}
        >
          {partners.map((partner, index) => (
            <div
              key={"partner-" + index}
              className="mb-2 flex items-center justify-start gap-3 overflow-y-auto rounded p-2 shadow-sm"
            >
              <Conditional if={hasOpenedOnce}>
                <BFLImage
                  src={partner.image}
                  width={18}
                  height={18}
                  alt={partner.imageAltText}
                  priority={false}
                  loading="lazy"
                  unoptimized={true}
                />
              </Conditional>
              <BFLLink
                href={partner.ctaLink}
                openInNewTab={partner.ctaInNewTab === "true"}
              >
                {partner.label}
              </BFLLink>
            </div>
          ))}
        </div>
      </BFLHoverBackdrop>
    </nav>
  );
};

export default PartnerLinksMenu;
